import React from 'react'
import { useTranslation } from 'react-i18next'
import { OptionList } from './OptionList'
import type { DisplayOptionDto } from '../services/display-option.service'
import './display-options-card.css'

/**
 * feat-004 (2026-10-13) — the CREATE-mode counterpart of `DisplayOptionsCard`.
 *
 * `DisplayOptionsCard` is server-driven: it fetches a product's options by
 * `productId` and every mutation it offers is keyed on an option `uuid`.
 * Neither exists while a product is still being created, which is why the
 * masterdata host hid the Display Options tab outright on Add New
 * (addnew.js, reported 2026-09-17 — the tab previously rendered a bare
 * "save the product first" placeholder, and a tab that can only tell you it
 * does nothing is worse than no tab).
 *
 * This component answers the same complaint the other way round: instead of a
 * placeholder, it shows the SIX ORIENTATION ROWS THEMSELVES, read-only, drawn
 * at the proportions of whatever W/H/D the user is typing on the Product
 * Information tab. They are not hypothetical — `ProductDaoImpl.save()` seeds
 * exactly these six rows in the same transaction as the product insert, so
 * what this previews is what the first Save actually writes.
 *
 * Read-only is deliberate, not a shortcut: per-option edits (dims, capacity,
 * stacking/hanging flags, dedicated images, set-default) all address a row by
 * its persisted uuid. Offering them here would mean inventing a client-side
 * staging format and a create-payload field for something the user can do
 * seconds later on the saved product.
 *
 * Drift note: this file deliberately encodes only ONE seeding rule — Front
 * (key 1) is the default. Everything else the rows display comes from
 * `OptionList`, which is the same component the saved-product card uses, so
 * the row labels, the isometric boxes and the default marker cannot disagree
 * between the preview and the real thing. The per-row stacking/hanging seed
 * rules live in the backend (`seed-default-orientations.ts`) and are NOT
 * re-implemented here — `deferred-work.md` already tracks three byte-identical
 * copies of the orientation permutation table drifting, and this is not a
 * fourth.
 */
export interface DisplayOptionsPreviewProps {
  /** Shown in the card header, same as the saved-product card. */
  productName?: string
  barcode?: string
  /**
   * The product's base W/H/D as currently entered. Unlike the saved-product
   * card — which must read the last PERSISTED snapshot so unsaved keystrokes
   * never leak into it (reported 2026-09-21) — the create-mode host has no
   * persisted snapshot to read, so this is sourced from the live form buffer
   * BY DESIGN. Partial/invalid input is expected while typing and is handled:
   * see `usableDims`.
   */
  baseDims?: {
    width: number | string | null
    height: number | string | null
    depth: number | string | null
  }
  isRTL?: boolean
}

/** The fixed orientation set, in the order the saved-product card lists them. */
const ORIENTATION_KEYS = [1, 2, 3, 4, 5, 6]

/**
 * The six rows `OptionList` will render. Only the fields that component
 * actually reads for an orientation row carry meaning here — `orientationKey`
 * (label + isometric proportions), `isDefault` (the star), `isEnabled` and
 * `archivedAt` (the lock reason) and `uuid` (React key). Dimensions are
 * deliberately null: an orientation row never stores its own dims in the
 * database either — they are derived at read time from the product's base
 * dims, which is exactly what `OptionList` does with `baseDims` below.
 */
const PREVIEW_OPTIONS: DisplayOptionDto[] = ORIENTATION_KEYS.map(
  (orientationKey) =>
    ({
      uuid: `preview-orientation-${orientationKey}`,
      type: 'orientation',
      orientationKey,
      name: null,
      width: null,
      height: null,
      depth: null,
      uom: null,
      capacity: null,
      isStackable: false,
      maxStackableCount: 0,
      isHangable: false,
      hangHoleX: null,
      hangHoleY: null,
      isEnabled: true,
      // The one seeding rule mirrored here: Front is the sole default.
      isDefault: orientationKey === 1,
      archivedAt: null,
    }) as DisplayOptionDto
)

/**
 * The form buffer holds strings, and holds partial ones mid-keystroke ("", "1.",
 * "-"). `deriveOrientationDims` is pure arithmetic and would happily propagate a
 * NaN into every isometric box, so dims are only handed over once all three are
 * real, positive, finite numbers. Until then `OptionList` receives no `baseDims`
 * and draws its plain cubes — the same fallback a host that never had dims gets.
 */
function usableDims(
  baseDims: DisplayOptionsPreviewProps['baseDims']
): { width: number; height: number; depth: number } | undefined {
  if (!baseDims) return undefined
  const width = Number(baseDims.width)
  const height = Number(baseDims.height)
  const depth = Number(baseDims.depth)
  const allUsable = [width, height, depth].every(
    (value) => Number.isFinite(value) && value > 0
  )
  return allUsable ? { width, height, depth } : undefined
}

export const DisplayOptionsPreview: React.FC<DisplayOptionsPreviewProps> = ({
  productName,
  barcode,
  baseDims,
  isRTL,
}) => {
  const { t } = useTranslation()
  const dims = usableDims(baseDims)

  return (
    <div
      className="simv3-doc"
      data-testid="display-options-preview"
      dir={isRTL ? 'rtl' : undefined}
    >
      {productName ? (
        <div className="simv3-doc__header" data-testid="display-options-header">
          {barcode ? (
            // Same no-interpolation rule as the saved-product card (reported
            // 2026-09-10): a product name containing a literal `"` (e.g. Hebrew
            // ס״מ) comes back HTML-entity-escaped if passed through t().
            <>
              {productName}{' — '}
              {t('simulationV3.displayOptions.header.barcodeLabel', {
                defaultValue: 'barcode',
              })}{' '}
              {barcode}
            </>
          ) : (
            productName
          )}
        </div>
      ) : null}

      <div className="simv3-doc__banner" data-testid="display-options-preview-banner">
        {t('simulationV3.displayOptions.preview.banner', {
          defaultValue:
            'These six orientations are created automatically when you save the product. You can configure them afterwards.',
        })}
      </div>

      <div className="simv3-doc__body">
        <OptionList
          options={PREVIEW_OPTIONS}
          selectedUuid={null}
          // Rows are inert before the product exists — there is no persisted
          // option to open a detail pane for.
          onSelect={() => undefined}
          baseImageUrl=""
          readOnly
          // No `onAddMode`: a display mode cannot be created before the
          // product it belongs to exists either.
          baseDims={dims}
        />
        <div className="simv3-doc__detail">
          <p className="simv3-doc__lock-reason">
            {t('simulationV3.displayOptions.preview.detailHint', {
              defaultValue:
                'Save the product to configure dimensions, stacking, hanging and images for each option.',
            })}
          </p>
        </div>
      </div>
    </div>
  )
}
